import {
  DeleteOutlined,
  FileTextOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  Input,
  Modal,
  Radio,
  Select,
  Steps,
  Tooltip,
  Upload,
  Progress,
  Button,
} from 'antd';
import { Alert } from '@insight-health-ai/roseate/components';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bars } from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureEntitlements, useAuth } from '@frontegg/react';
import {
  createAutoTemplate,
  createAutoTemplateSuccess,
} from '../../store/actions/autotemplate.action';
import './TemplateOverrideModal.scss';
import {
  autoTemplateDraftUuidSlice,
  createdAutoTemplateSlice,
} from '../../store/slices/autotemplate.slice';
import { SupaDemo } from '../../../../components/groupComponents/supaDemo';
import { analytics } from 'src/services';
import { getAutoTemplateDraft } from '../../store/actions/autotemplate.action';
import { usePolling } from 'src/modules/ambient_solo/hooks/usePolling';

const { Step } = Steps;

function FileDocument({ document, onDelete }) {
  const prefixIcon = <FileTextOutlined />;

  return (
    <div className="flex bg-white border rounded-md p-3">
      <div className="flex items-center flex-shrink-0 text-lg">
        {prefixIcon}
      </div>
      <div className="flex flex-col items-start flex-grow px-3 gap-1">
        <div className="text-md font-medium text-primary">
          {document.name}
          <div className="text-sm text-gray-500">
            Size: {((document.size || 0) / 1000).toFixed(2)} KB
          </div>
        </div>
      </div>
      <div className="flex items-center flex-shrink-0">
        <Tooltip title="Delete">
          <button
            className="flex gap-2 text-lg cursor-pointer"
            onClick={onDelete}
          >
            <DeleteOutlined className="text-[#d63232]" />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

const AutoTemplates = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [fileUploading, setFileUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [templateType, setTemplateType] = useState('');
  const [progress, setProgress] = useState(0);
  const [autoTemplateGenerating, setAutoTemplateGenerating] = useState(false);
  const [showPasteNotesModal, setShowPasteNotesModal] = useState(false);
  const [pasteNotesContent, setPasteNotesContent] = useState('');
  const [autoTemplateDraft, setAutoTemplateDraft] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [progressTimer, setProgressTimer] = useState(null);

  const { user } = useAuth();

  const { isEntitled: hasAutoTemplateAccess } =
    useFeatureEntitlements('auto-template');

  const { createdAutoTemplate, autoTemplateDraftUuid } = useSelector(
    (state) => state?.autotemplateState
  );

  const pollAutoTemplateDraft = useCallback(async () => {
    if (!autoTemplateDraft || !isPolling) return;

    try {
      const response = await getAutoTemplateDraft(
        autoTemplateDraft,
        dispatch
      )();

      if (response && Object.keys(response).length > 0) {
        setIsPolling(false);
        setFileUploading(false);
        setProgress(100);
        if (progressTimer) {
          clearInterval(progressTimer);
          setProgressTimer(null);
        }
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error polling auto template draft:', error);
      }
    }
  }, [autoTemplateDraft, dispatch, progressTimer, isPolling]);

  usePolling(
    {
      pollingFunction: pollAutoTemplateDraft,
      calculateInterval: () => 15000,
      immediate: false,
      defaultInterval: 15000,
    },
    [autoTemplateDraft]
  );

  useEffect(() => {
    if (autoTemplateDraftUuid) {
      setAutoTemplateDraft(autoTemplateDraftUuid);
      setIsPolling(true);
    } else {
      setAutoTemplateDraft(null);
      setIsPolling(false);
    }
  }, [autoTemplateDraftUuid]);

  useEffect(() => {
    return () => {
      if (progressTimer) {
        clearInterval(progressTimer);
        setProgressTimer(null);
      }
      dispatch(autoTemplateDraftUuidSlice(null));
      dispatch(createdAutoTemplateSlice({}));
      setAutoTemplateDraft(null);
      setIsPolling(false);
    };
  }, [dispatch]);

  const templateTypeOptions = [
    { value: 'pre-chart', label: 'Pre-chart' },
    { value: 'in-visit', label: 'In visit' },
    { value: 'after-visit', label: 'Documents' },
  ];

  const renderDocument = (attachment, index) => {
    return (
      <FileDocument
        {...attachment}
        onDelete={() => {
          setAttachments(attachments.filter((_, i) => i !== index));
        }}
      />
    );
  };

  const createFileDocument = async (file) => {
    analytics.track(`Uploaded File (${file.type}) In Auto Template`, {
      email: user?.email,
    });
    setIsLoading(true);
    try {
      const fileObj = file.originFileObj || file;
      setAttachments([
        ...attachments,
        {
          document: {
            name: fileObj.name,
            type: 'file',
            mime_type: fileObj.type,
            size: fileObj.size,
          },
          fileToUpload: fileObj,
        },
      ]);
    } catch (error) {
      console.error('Error creating file document:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadClick = () => {
    if (
      !hasAutoTemplateAccess ||
      (currentStep === 0 && attachments.length === 0)
    ) {
      return;
    }
    analytics.track('Clicked Upload In Auto Template', {
      email: user?.email,
    });
    if (currentStep === 0 && attachments.length >= 1) {
      setProgress(0);
      const payload = {
        files: attachments
          .filter((attachment) => attachment.document.type === 'file')
          .map((attachment) => attachment.fileToUpload),
        content: attachments
          .filter((attachment) => attachment.document.type === 'text')
          .map((attachment) => attachment.document.content)
          .join('\n\n'),
      };
      setFileUploading(true);
      const timer = setInterval(() => {
        setProgress((prevProgress) => Math.min(prevProgress + 1, 99));
      }, 800);
      setProgressTimer(timer);
      createAutoTemplate(payload, dispatch)
        .then((_response) => {
          setIsPolling(true);
        })
        .catch((error) => {
          console.log('error', error);
          setAttachments([]);
          setTemplateType('');
          dispatch(createdAutoTemplateSlice({}));
          dispatch(autoTemplateDraftUuidSlice(null));
          setAutoTemplateDraft(null);
          setCurrentStep(0);
          setIsPolling(false);
          if (progressTimer) {
            clearInterval(progressTimer);
            setProgressTimer(null);
          }
        });
      setCurrentStep(1);
    }
  };

  const handleAddNote = () => {
    if (pasteNotesContent.trim()) {
      setAttachments([
        ...attachments,
        {
          document: {
            content: pasteNotesContent.trim(),
            type: 'text',
          },
        },
      ]);
      setShowPasteNotesModal(false);
      setPasteNotesContent('');
    }
  };

  const handleCancelNote = () => {
    setShowPasteNotesModal(false);
    setPasteNotesContent('');
  };

  const steps = [
    {
      title: 'Upload notes',
      content: (
        <>
          <div className="flex flex-col min-height: 400px">
            {/* Info Alert */}
            <div className="mb-6 flex justify-center">
              <div className="w-full">
                <Alert
                  message={<span>Why upload multiple notes?</span>}
                  description={
                    <span>
                      Our AI analyzes patterns across your notes to understand
                      your unique documentation style, preferred terminology,
                      and structural preferences. The more examples you provide,
                      the better your custom template will match your workflow.
                    </span>
                  }
                  icon={true}
                  closable={false}
                />
              </div>
            </div>

            {/* Progress Steps */}
            <div className="mb-6 flex justify-center">
              <div className="w-full">
                <Steps
                  current={attachments.length}
                  size="small"
                  className="mb-3"
                >
                  <Step title="" />
                  <Step title="" />
                  <Step title="" />
                </Steps>
                <div className="text-center text-gray-600 text-sm">
                  Upload {Math.max(0, 3 - attachments.length)} more notes for
                  optimal results
                </div>
              </div>
            </div>

            <Upload.Dragger
              disabled={
                isLoading || attachments.length >= 3 || !hasAutoTemplateAccess
              }
              fileList={[]}
              name="files"
              accept=".docx,.txt,.pdf"
              multiple={true}
              beforeUpload={(file, fileList) => {
                // Only process files if we haven't reached the limit
                if (attachments.length + fileList.length <= 3) {
                  fileList.forEach((file) => {
                    createFileDocument(file);
                  });
                }
                return false;
              }}
              style={{
                height: '100%',
                minHeight: '200px',
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                backgroundColor: '#FAFDFF',
              }}
              className={attachments.length >= 3 ? 'opacity-50' : ''}
            >
              <div className="flex flex-col justify-center items-center h-full min-h-[200px] py-8">
                <p className="ant-upload-drag-icon text-4xl mb-4 text-gray-400">
                  <InboxOutlined />
                </p>
                {attachments.length >= 3 ? (
                  <>
                    <p className="ant-upload-text text-lg mb-2 text-gray-600 font-medium">
                      Maximum notes uploaded
                    </p>
                    <p className="text-sm text-gray-500">
                      or click to browse • PDF, Word, images, or text files
                    </p>
                  </>
                ) : (
                  <>
                    <p className="ant-upload-text text-lg mb-2">
                      Click or drag files to this area to upload
                    </p>
                    <p className="text-sm text-gray-500">
                      txt, docx, pdf (max 3 files)
                    </p>
                    <p className="font-semibold text-lg text-gray-400 text-center mt-2">
                      OR
                    </p>
                    <div className="flex justify-center mt-2">
                      <button
                        className="ih-button-primary gap-2"
                        onClick={(e) => {
                          analytics.track(
                            'Clicked Paste Notes In Auto Template',
                            {
                              email: user?.email,
                            }
                          );
                          e.stopPropagation();
                          setShowPasteNotesModal(true);
                          setPasteNotesContent('');
                        }}
                        disabled={
                          attachments.length >= 3 || !hasAutoTemplateAccess
                        }
                      >
                        <FileTextOutlined />
                        Paste Notes
                      </button>
                    </div>
                  </>
                )}
              </div>
            </Upload.Dragger>
          </div>
          {attachments.filter(
            (attachment) => attachment.document.type !== 'text'
          ).length > 0 && (
            <>
              <div className="text-lg font-semibold text-gray-800 mt-4">
                Uploaded Files
              </div>
              <div className="flex flex-col-reverse gap-4 max-h-[400px] overflow-y-auto mt-2">
                {attachments.map(
                  (attachment, index) =>
                    attachment.document.type !== 'text' && (
                      <div key={index}>{renderDocument(attachment, index)}</div>
                    )
                )}
              </div>
            </>
          )}

          {attachments.filter(
            (attachment) => attachment.document.type === 'text'
          ).length > 0 && (
            <>
              <div className="text-lg font-semibold text-gray-800 mt-6 mb-3">
                Uploaded Notes
              </div>
              <div className="flex flex-col gap-3">
                {attachments.map(
                  (attachment, index) =>
                    attachment.document.type === 'text' && (
                      <div
                        key={`text-${index}`}
                        className="flex bg-gray-50 border border-gray-200 rounded-lg p-3"
                      >
                        <div className="flex items-center flex-shrink-0 text-lg text-amber-600 mr-3">
                          <FileTextOutlined />
                        </div>
                        <div className="flex flex-col items-start flex-grow">
                          <div className="text-md font-medium text-gray-800">
                            Pasted Note{' '}
                            {attachments
                              .filter((a) => a.document.type === 'text')
                              .findIndex((a) => a === attachment) + 1}
                          </div>
                        </div>
                        <div className="flex items-center flex-shrink-0">
                          <Tooltip title="Delete">
                            <button
                              className="flex gap-2 text-lg cursor-pointer"
                              onClick={() => {
                                setAttachments(
                                  attachments.filter((_, i) => i !== index)
                                );
                              }}
                            >
                              <DeleteOutlined className="text-[#d63232]" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    )
                )}
              </div>
            </>
          )}

          {/* Generate Template Button */}
          <div className="mt-8 flex justify-center">
            <Button
              size="large"
              onClick={handleUploadClick}
              disabled={attachments.length === 0}
              className={`px-8 py-3 ${
                attachments.length === 0
                  ? 'ih-button-ghost'
                  : 'ih-button-primary'
              }`}
            >
              <span className="mr-2">Generate My Template →</span>
            </Button>
          </div>
        </>
      ),
    },
    {
      title: 'Template info',
      content: (
        <>
          <div className="flex items-center justify-center h-full flex-col">
            <div className="w-full max-w-[600px] flex flex-col gap-4">
              <div className="flex flex-row gap-7 items-center">
                <span className="text-sm whitespace-nowrap text-black">
                  Template type
                </span>
                <Select
                  options={templateTypeOptions}
                  placeholder="Select template type"
                  onChange={(value) => setTemplateType(value)}
                  className="w-full"
                />
              </div>
            </div>
            {fileUploading && (
              <div className="max-w-[800px] mt-8 w-full">
                <div className="w-full">
                  <div className="text-sm text-black mb-2">
                    {progress <= 20
                      ? 'Uploading...'
                      : progress > 20 && progress <= 80
                        ? 'Generating sections...'
                        : progress >= 80 && progress <= 100
                          ? 'Adding instructions...'
                          : 'Finalizing template...'}
                  </div>
                  <Progress
                    percent={Number(progress.toFixed(1))}
                    status="active"
                    strokeColor="#00D090"
                    strokeWidth={10}
                  />
                  <div className="text-sm text-black mt-2 text-center">
                    This usually takes 2-3 minutes
                  </div>
                </div>
              </div>
            )}
            <div className="mt-4 min-w-full">
              {!fileUploading && (
                <>
                  <div className="flex justify-center items-center h-full mt-4 w-full">
                    <div className="border-0  p-2 px-4 bg-[#f8f8f8] rounded-xl ">
                      {createdAutoTemplate?.template_data
                        ?.custom_instructions && (
                        <div className="custom-instructions">
                          <div className="text-lg mt-2 py-2 font-medium">
                            Custom Instructions
                          </div>
                          <div className="border-0 text-xs mb-2 py-2 px-6 rounded-xl bg-white">
                            {
                              createdAutoTemplate?.template_data
                                ?.custom_instructions
                            }
                          </div>
                        </div>
                      )}
                      <div className="text-xl py-3 font-medium mt-2">
                        Sections
                      </div>
                      <div className={`overflow-auto h-128px`}>
                        {createdAutoTemplate?.template_data?.sections?.map(
                          (sec, j) => (
                            <div key={j}>
                              <div className="border-0 text-sm mb-2 font-medium py-2 px-6 rounded-xl bg-white">
                                {sec.name}
                              </div>
                              <div className="ml-2 pb-5 text-xs text-gray-600">
                                Custom instruction: {sec.instructions}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                      {createdAutoTemplate?.template_data?.format && (
                        <div className="format mb-4 ">
                          <div className="text-lg  mt-2 py-2 font-medium">
                            Format
                          </div>
                          <div>
                            <Radio.Group
                              value={createdAutoTemplate?.template_data?.format}
                            >
                              <Radio
                                value={
                                  createdAutoTemplate?.template_data?.format
                                }
                                checked
                              >
                                {createdAutoTemplate?.template_data?.format
                                  .charAt(0)
                                  .toUpperCase() +
                                  createdAutoTemplate?.template_data?.format.slice(
                                    1
                                  )}
                              </Radio>
                            </Radio.Group>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-6 flex justify-center items-center gap-4">
                    <button
                      className="ih-button-negative gap-2"
                      onClick={() => {
                        analytics.track(
                          'Clicked Discard Template In Auto Template',
                          {
                            email: user?.email,
                          }
                        );
                        setAttachments([]);
                        setTemplateType('');
                        setCurrentStep(0);
                        dispatch(autoTemplateDraftUuidSlice(null));
                        dispatch(createdAutoTemplateSlice({}));
                      }}
                    >
                      <DeleteOutlined />
                      Discard
                    </button>
                    <Tooltip
                      title={
                        templateType === ''
                          ? 'Please select a template type to continue'
                          : ''
                      }
                    >
                      <div>
                        {' '}
                        {/* Wrapper div needed for disabled button tooltip */}
                        <button
                          className="ih-button-primary gap-2"
                          disabled={templateType === ''}
                          onClick={() => {
                            analytics.track(
                              'Clicked Generate Template In Auto Template',
                              {
                                email: user?.email,
                              }
                            );
                            setAutoTemplateGenerating(true);
                            dispatch(
                              createAutoTemplateSuccess(
                                {
                                  template_name: `Auto template ${new Date().toLocaleDateString(
                                    'en-US'
                                  )}`,
                                  template_type: templateType,
                                  auto_template_result_uuid: autoTemplateDraft,
                                },
                                (template_overrides_id) => {
                                  setAutoTemplateGenerating(false);
                                  const template_type = templateType;
                                  setAttachments([]);
                                  setTemplateType('');
                                  dispatch(createdAutoTemplateSlice({}));
                                  navigate(
                                    `/setting?encounter-phase=${template_type}&template_overrides_id=${template_overrides_id}`
                                  );
                                },
                                () => {
                                  setAutoTemplateGenerating(false);
                                }
                              )
                            );
                          }}
                        >
                          <FileTextOutlined />
                          Generate template
                        </button>
                      </div>
                    </Tooltip>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <div className={`page-wrapper h-full`}>
        <div
          className={`relative ${autoTemplateGenerating ? 'blur-[1px]' : ''}`}
        >
          <div className="p-5">
            <div className="flex justify-start gap-1">
              <SupaDemo>
                <span
                  data-supademo-demo="cm73q8pgk0nidj003ha5780es"
                  className="cursor-pointer ml-[-16px] ih-button-ghost gap-2 text-[#5a8dda] underline"
                  onClick={() => {
                    analytics.track('Clicked Auto Template Guided Tour ', {
                      email: user?.email,
                    });
                  }}
                >
                  <QuestionCircleOutlined /> Guided Tour
                </span>
              </SupaDemo>
            </div>
          </div>
          <div className="pb-5 w-[70%] mx-auto">
            <div className="bg-white rounded-lg shadow-md p-8">
              {currentStep === 0 ? steps[0].content : steps[1].content}
            </div>
          </div>
        </div>
        {autoTemplateGenerating && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/30">
            <Bars
              height="30"
              width="30"
              color="#00D090"
              ariaLabel="bars-loading"
              wrapperStyle={{}}
              wrapperClass="justify-center"
              visible={true}
            />
          </div>
        )}
      </div>

      {/* Paste Notes Modal */}
      <Modal
        title="Paste Clinical Note"
        open={showPasteNotesModal}
        onCancel={handleCancelNote}
        footer={[
          <Button key="cancel" onClick={handleCancelNote}>
            Cancel
          </Button>,
          <Button
            key="add"
            type="primary"
            onClick={handleAddNote}
            disabled={!pasteNotesContent.trim()}
            style={{ backgroundColor: '#00D090', borderColor: '#00D090' }}
          >
            Add Note
          </Button>,
        ]}
        width={600}
        centered
        destroyOnClose
      >
        <div className="mb-4">
          <p className="text-gray-600 mb-4">
            Copy a clinical note from another system and paste it below.
          </p>
          <Input.TextArea
            placeholder="Paste your clinical note here..."
            value={pasteNotesContent}
            onChange={(e) => setPasteNotesContent(e.target.value)}
            rows={8}
            style={{ resize: 'none' }}
            autoFocus
          />
        </div>
      </Modal>
    </>
  );
};

export default AutoTemplates;
